import { useRouter } from 'next/router';
import type { NextPageContext } from 'next';
import React, { ComponentType, useState, useEffect } from 'react';

import {
  capitalizeFirstLetter,
  getParameterByName,
  combinePredicatesUsingOr,
  constructQueryStringFromFilters,
} from 'utils/utils';
import { productListFiltered } from 'utils/metrics';
import productCategories, { CategoryDto } from 'data/Products';
import FilterComponent from 'partials/products/FilterComponent';
import CollectionList from 'partials/products/CollectionList';
import { getProducts, getBundles } from 'services/productsApi';
import { Item, ProductCategoryFilter, PublishedPage } from 'types';
import { CollectionPageContents } from '@root/sanity/sanity.types';
import DoubleHeader from 'components/typography/DoubleHeader';
import { withPageStaticProps } from 'utils/header';
import TuxedosAndSuitsCopy from 'components/collection/tuxedos-and-suits';
import TiesCopy from 'components/collection/ties';
import AccessoriesCopy from 'components/collection/accessories';
import VestsAndCummerbundsCopy from 'components/collection/vests-and-cummerbunds';
import ShoesAndSocksCopy from 'components/collection/shoes-and-socks';
import ShirtsCopy from 'components/collection/shirts';
import { getPublishedPages } from '@root/server/services/Pages';
import { PortableText } from 'components/sanity/serializers';
import Layout1x1 from 'components/Layout1x1';
import type { ColorFilter } from '@root/src/partials/Visualizer';

type SanityCollectionPageContents = Omit<CollectionPageContents, '_type'>;

type RenderToPortableTextProps = {
  sanityData: SanityCollectionPageContents[];
  className?: string;
};

const RenderToPortableText = ({ className = '', sanityData }: RenderToPortableTextProps) => (
  <div className={className}>
    <PortableText content={sanityData} />
  </div>
);

const getPublishedPage = async (slug: string) => {
  const pages = await getPublishedPages();
  const foundPage = pages.filter((page: PublishedPage) => page.slug.current === slug);

  return foundPage[0];
};

const getMarketingCopy = async (collectionSlug: string) => {
  const page = await getPublishedPage(collectionSlug);

  const pageBuilder: any = page?.pageBuilder;

  if (!pageBuilder) return null;

  const collectionPageContents: SanityCollectionPageContents[] = pageBuilder.map((el: CollectionPageContents) => {
    if (el?._type === 'collectionPageContents') return el;
  });

  const { collectionCta, collectionContent, collectionCategory } = collectionPageContents[0];

  return {
    collectionCategory: collectionCategory,
    collectionCta: collectionCta,
    collectionContent: collectionContent,
  };
};

const getCategoryFromSlug = (categorySlug: string) =>
  productCategories.find((category) => category.slug === categorySlug);

const parseFiltersFromUrl = (filters: string) =>
  decodeURIComponent(filters)
    .split(',')
    .map((filter) => filter.replace(/\+/g, ' ').split(' ').map(capitalizeFirstLetter).join(' '));

const slugToAboutContent: { [key: string]: ComponentType } = {
  accessories: AccessoriesCopy,
  shirts: ShirtsCopy,
  'shoes-and-socks': ShoesAndSocksCopy,
  ties: TiesCopy,
  'tuxedos-and-suits': TuxedosAndSuitsCopy,
  'vests-and-cummerbunds': VestsAndCummerbundsCopy,
};

const AboutContent = ({ category }: { category: string | null }) => {
  if (!category || !(category in slugToAboutContent)) {
    return null;
  }

  const CopyComponent = slugToAboutContent[category];

  return (
    <div className="bg-gray-lighter py-64 sm:py-128">
      <CopyComponent />
    </div>
  );
};

type HeaderContentProps = {
  displayName: string;
  activeCategory: CategoryDto;
};

const headerContent = (props: HeaderContentProps) => (
  <>
    <div className="col-start-1 col-end-13 sm:col-start-4 md:col-start-3">
      <div className="mb-32 xs:mb-64">
        <DoubleHeader
          hero
          smallTitle={<h2>Collection</h2>}
          largeTitle={
            <h1>{props.displayName === 'Tuxedos & Suits' ? "Men's Suits and Tuxedos" : props.displayName}</h1>
          }
        />

        {props.activeCategory.name === 'preconfigured' && (
          <p className="mt-16 max-w-md">
            Choose from 25+ men's suit and tuxedo styles designed in trending colors and fashionable fits.
          </p>
        )}
      </div>
    </div>
  </>
);

interface Props {
  activeCategory: CategoryDto;
  products: Item[];
  marketingCopy?: any;
}

export const List = ({ activeCategory, products, marketingCopy }: Props) => {
  const [categoryFilters, setCategoryFilters] = useState<ProductCategoryFilter[]>([]);
  const [colorFilters, setColorFilters] = useState<string[]>([]);
  const [patternFilters, setPatternFilters] = useState<string[]>([]);
  const [procurementFilters, setProcurementFilters] = useState<string[]>([]);
  const [tieTypeFilters, setTieTypeFilters] = useState<string[]>([]);

  const router = useRouter();

  useEffect(() => {
    const type = getParameterByName('type');
    const tieType = getParameterByName('tieType');
    const color = getParameterByName('color');
    const pattern = getParameterByName('pattern');
    const procurement = getParameterByName('procurement');

    // Need to get category filters for type, should look in products.js
    const allCategoryFilters = type ? getFiltersMatchingActiveCategory(productCategories) : [];
    const categoryFilters = allCategoryFilters.filter((filter) =>
      parseFiltersFromUrl(type || '').includes(filter.displayName)
    );
    const tieTypeFilters = tieType ? parseFiltersFromUrl(tieType) : [];
    const colorFilters: ColorFilter[] = color ? (parseFiltersFromUrl(color) as ColorFilter[]) : [];
    const patternFilters = pattern ? parseFiltersFromUrl(pattern) : [];
    const procurementFilters = procurement ? parseFiltersFromUrl(procurement) : [];

    setCategoryFilters(categoryFilters);
    setColorFilters(colorFilters);
    setPatternFilters(patternFilters);
    setProcurementFilters(procurementFilters);
    setTieTypeFilters(tieTypeFilters);
  }, [activeCategory]);

  const getFiltersMatchingActiveCategory = (categories: CategoryDto[]): ProductCategoryFilter[] => {
    const matchingCategory = categories.find((category) => category.displayName.includes(activeCategory.displayName));

    if (!matchingCategory) {
      return [];
    }

    return matchingCategory.categoryFilters;
  };

  const trackProductListFiltered = () =>
    productListFiltered(
      activeCategory.name,
      {
        categories: categoryFilters,
        colors: colorFilters,
        patterns: patternFilters,
        procurement: procurementFilters,
        tieTypes: tieTypeFilters,
      },
      getFilteredProducts()
    );

  // useEffect hook for updating URL
  useEffect(() => {
    const filtersMap = {
      color: colorFilters,
      pattern: patternFilters,
      type: categoryFilters,
      procurement: procurementFilters,
      tieType: tieTypeFilters,
      // Add your new filter here. It can be an array or a single value.
    };

    const queryString = constructQueryStringFromFilters(filtersMap);
    let newRoute = window.location.pathname;

    if (queryString) {
      newRoute += `?${queryString}`;
    }

    router.replace(newRoute, undefined, { shallow: true, scroll: false });
  }, [colorFilters, patternFilters, categoryFilters, procurementFilters, tieTypeFilters]);

  const toggleColorFilters = (colorFilter: string) => {
    const nextColorFilters = colorFilters.includes(colorFilter)
      ? colorFilters.filter((c) => c !== colorFilter)
      : [...colorFilters, colorFilter];

    setColorFilters(nextColorFilters);
    trackProductListFiltered();
  };

  const togglePatternFilters = (patternFilter: string) => {
    setPatternFilters(
      patternFilters.includes(patternFilter)
        ? patternFilters.filter((p) => p !== patternFilter)
        : patternFilters.concat(patternFilter)
    );

    trackProductListFiltered();
  };

  const toggleCategoryFilters = (categoryFilter: ProductCategoryFilter) => {
    setCategoryFilters(
      categoryFilters.includes(categoryFilter)
        ? categoryFilters.filter((filter) => filter.displayName !== categoryFilter.displayName)
        : categoryFilters.concat(categoryFilter)
    );

    trackProductListFiltered();
  };

  const toggleProcurementFilters = (procurementFilter: string) => {
    setProcurementFilters(
      procurementFilters.includes(procurementFilter)
        ? procurementFilters.filter((r) => r !== procurementFilter)
        : procurementFilters.concat(procurementFilter)
    );

    trackProductListFiltered();
  };

  const toggleTieTypesFilters = (tieTypeFilter: string) => {
    setTieTypeFilters(
      tieTypeFilters.includes(tieTypeFilter)
        ? tieTypeFilters.filter((filter) => filter !== tieTypeFilter)
        : tieTypeFilters.concat(tieTypeFilter)
    );

    trackProductListFiltered();
  };

  const getFilteredProducts = () => {
    const filteredProducts = products.filter((product) => {
      // Keep products that match category filters.
      if (categoryFilters.length > 0) {
        const combinedPredicates = combinePredicatesUsingOr(categoryFilters.map((filter) => filter.value));

        const values = [product.display_name!];

        if (product.category !== 'Tie') {
          values.push(product.category!);
        }

        if (product.category === 'preconfigured' && product.type) {
          values.push(product.type);
        }

        const hasMatchingCategory = values.some(combinedPredicates);

        if (!hasMatchingCategory) {
          return false;
        }
      }

      // Keep products that match pattern filters.
      if (patternFilters.length > 0) {
        if (!patternFilters.includes(product.pattern!)) {
          return false;
        }
      }

      // Keep products that match color filters.
      // Removed special exception for brown bundles.
      if (colorFilters.length > 0) {
        if (!colorFilters.includes(product.color as ColorFilter)) {
          return false;
        }
      }

      // Keep products that match tieType filters.
      if (tieTypeFilters.length > 0) {
        if (!tieTypeFilters.includes(product.attributeSecondary!)) {
          return false;
        }
      }

      // Keep products that match procurement filters.
      if (procurementFilters.length === 1) {
        const lowerCaseProcurementFilters = procurementFilters.map((filter) => filter.toLowerCase());

        if (lowerCaseProcurementFilters.includes('buy')) {
          return product.is_retail || product.retail_bundle?.displayable || product.type?.toLowerCase() === 'purchase';
        }

        if (lowerCaseProcurementFilters.includes('rent')) {
          return product.is_retail === false || product.type?.toLowerCase() === 'rental';
        }
      }

      return true;
    });

    return sortProducts(filteredProducts);
  };

  // we are currently defaulting to sort pocket squares first when applicable
  const sortProducts = (filteredProducts: Item[]) => {
    sortByDisplayIndex(filteredProducts);

    if (activeCategory.name.includes('Pocket Square')) {
      sortPocketSquares(filteredProducts);
    }

    return filteredProducts;
  };

  const sortPocketSquares = (products: Item[]) => {
    return products.sort((a, b) => {
      const haveMatchingCategories = a.category === b.category;

      if (a.category === 'Pocket Square' && !haveMatchingCategories) {
        return -1;
      } else if (b.category === 'Pocket Square' && !haveMatchingCategories) {
        return 1;
      }

      return 0;
    });
  };

  const sortByDisplayIndex = (products: Item[]) => {
    return products.sort((a, b) => {
      const aDisplayIndex = Number(a.display_index ?? 999);
      const bDisplayIndex = Number(b.display_index ?? 999);

      return aDisplayIndex - bDisplayIndex;
    });
  };

  const filteredProducts = getFilteredProducts();

  return (
    <>
      <div className="product-index-page container py-32 sm:py-64">
        <div className="row">
          {marketingCopy && activeCategory.slug === marketingCopy?.collectionCategory ? (
            <div className="col-start-1 col-end-13 sm:col-start-4 md:col-start-3">
              <RenderToPortableText sanityData={marketingCopy.collectionCta} className="mb-32 xs:mb-64" />
            </div>
          ) : (
            headerContent({ displayName: activeCategory.displayName, activeCategory: activeCategory })
          )}
        </div>
        <div className="row">
          <div className="col-start-1 col-end-13 mb-64 xs:col-end-4 sm:col-end-4 md:col-end-3">
            <FilterComponent
              activeCategory={activeCategory}
              categoryFilters={categoryFilters}
              colorFilters={colorFilters}
              patternFilters={patternFilters}
              procurementFilters={procurementFilters}
              products={products}
              tieTypeFilters={tieTypeFilters}
              toggleCategoryFilters={(category: ProductCategoryFilter) => toggleCategoryFilters(category)}
              toggleColorFilters={(color: string) => toggleColorFilters(color)}
              togglePatternFilters={(pattern: string) => togglePatternFilters(pattern)}
              toggleProcurementFilters={(procurementType: string) => toggleProcurementFilters(procurementType)}
              toggleTieTypeFilters={(tieTypeFilter: string) => toggleTieTypesFilters(tieTypeFilter)}
            />
          </div>

          <div className="col-start-1 col-end-13 xs:col-start-4 sm:col-start-4 md:col-start-3">
            {filteredProducts.length > 0 ? (
              <CollectionList
                products={filteredProducts}
                activeCategory={activeCategory}
                activeFilter={
                  categoryFilters.length > 0 ||
                  colorFilters.length > 0 ||
                  patternFilters.length > 0 ||
                  procurementFilters.length > 0 ||
                  tieTypeFilters.length > 0
                }
                tuxOrSuits={activeCategory.name === 'preconfigured'}
              />
            ) : (
              <div>
                <h3 className="text-h3">No matches</h3>
                <p>Try selecting different filters.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {marketingCopy && activeCategory.slug === marketingCopy?.collectionCategory ? (
        <div className="bg-gray-lighter py-64 sm:py-128">
          <Layout1x1>
            <RenderToPortableText sanityData={marketingCopy.collectionContent} />
          </Layout1x1>
        </div>
      ) : (
        <AboutContent category={activeCategory.slug ?? null} />
      )}
    </>
  );
};

const getCollection = async (category: string) => {
  const isBundle = category.toLowerCase() === 'preconfigured';

  const response = isBundle ? await getBundles() : await getProducts(category);

  const items = await response.json();

  const collectionFilter = (item: any): boolean => {
    // Exclude recommended items
    if (item.display_name.includes('RECOMMENDED')) return false;
    // Exclude items that are not meant to be displayed
    if (!item.displayable) return false;
    // Exclude items with no pictures/videos
    if (item.media.length === 0) return false;
    // Exclude retail items that are associated with a rental bundle
    if (item.is_retail && !!item.rental_bundle) return false;

    return true;
  };

  return items.filter(collectionFilter);
};

// This function gets called at build time
export const getStaticPaths = async () => {
  const categories = productCategories.filter((c) => c.slug !== null);

  const paths = categories.map((c) => ({
    params: {
      category: c.slug,
    },
  }));

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
};

interface Context extends NextPageContext {
  params: {
    category: string;
  };
}

export const getStaticProps = withPageStaticProps(async ({ params }: Context) => {
  const { category } = params;

  const productCategory = getCategoryFromSlug(category);

  const collectionSlug = category === 'tuxedos-and-suits' ? 'collection' : `collection/${category}`;

  return {
    props: {
      activeCategory: {
        name: productCategory!.name,
        displayName: productCategory!.displayName,
        slug: category,
      },
      products: await getCollection(productCategory!.name),
      marketingCopy: await getMarketingCopy(collectionSlug),
    },
  };
});

export default List;
